import { lazy } from 'react';

import PageLayout from '@c/layouts/pages/page.component';
import PageOutletLayout from '@c/layouts/pages/page-outlet.component';

import { bluebookDashboardLoader } from '@p/a/actions/bluebook-dashboard.loader';
import { returnReportLoader } from '@p/a/actions/return-report.loader';
import CagingCalendarProvider from '@p/a/r/calendar/context/calendar.provider';
import { listPerformanceLoader } from '@p/a/r/lp/actions/list-performance.loader';

import { Route } from '@t/routes';

import { lazyNamed } from '@u/lazy-named';
import { s, t } from '@u/strings';

import { RouteGuard } from 'hoc';

const AnalyticsPage = lazyNamed(() => {
  return import('pages/analytics');
}, 'AnalyticsPage');
const BluebookDashboard = lazy(() => {
  return import('pages/analytics/bluebook/bluebook-dashboard');
});
const CagingCalendarPage = lazy(() => {
  return import('pages/analytics/reports/calendar/caging-calendar.page');
});
const CagingCalendarDayPage = lazy(() => {
  return import('pages/analytics/reports/calendar/pages/day/day.page');
});
const CagingCalendarMonthPage = lazy(() => {
  return import('pages/analytics/reports/calendar/pages/month/month.page');
});
const CagingCalendarYearPage = lazy(() => {
  return import('pages/analytics/reports/calendar/pages/year/year.page');
});
const ListPerformancePage = lazy(() => {
  return import('pages/analytics/reports/list-performance/list-performance.page');
});
const ReturnReport = lazy(() => {
  return import('pages/analytics/client-report/return-report');
});
const ShippingLogPage = lazy(() => {
  return import('pages/analytics/reports/shipping-log/shipping-log.page');
});

export const analyticsRoute: Route = {
  children: [
    {
      children: [
        {
          children: [
            {
              element: <BluebookDashboard />,
              index: true,
              loader: bluebookDashboardLoader,
            },
            {
              element: <BluebookDashboard />,
              loader: bluebookDashboardLoader,
              path: 'Root.children.Analytics.children.Bluebooks.dashboard.children.filter',
            },
          ],
          element: (
            <PageOutletLayout
              documentTitle={t('Site.Pages.Bluebook.DashBoard')}
              title={s('Site.Pages.Bluebook.DashBoard')}
            />
          ),
          icon: ['fas', 'book'],
          includeInMenu: true,
          path: 'Root.children.Analytics.children.Bluebooks.dashboard.path',
          roles: ['devops'],
          title: s('Site.Pages.Analytics.Bluebook'),
        },
      ],
      element: <RouteGuard role={['devops']} />,
    },
    {
      children: [
        {
          children: [
            {
              element: <ReturnReport />,
              index: true,
              loader: returnReportLoader,
            },
          ],
          element: <PageOutletLayout documentTitle={t('Site.Pages.Bluebook.ClientDashboard')} />,
          includeInMenu: false,
          path: 'Root.children.Analytics.children.Bluebooks.report',
        },
      ],
      element: <RouteGuard role={['devops']} />,
    },
    {
      children: [
        {
          children: [
            {
              children: [
                {
                  element: <CagingCalendarMonthPage />,
                  handle: {
                    type: 'month',
                  },
                  index: true,
                },
                {
                  element: <CagingCalendarDayPage />,
                  handle: {
                    type: 'day',
                  },
                  path: 'Root.children.Analytics.children.Reports.children.CagingCalendar.children.day',
                },
                {
                  element: <CagingCalendarMonthPage />,
                  handle: {
                    type: 'month',
                  },
                  path: 'Root.children.Analytics.children.Reports.children.CagingCalendar.children.month',
                },
                {
                  element: <CagingCalendarYearPage />,
                  handle: {
                    type: 'year',
                  },
                  path: 'Root.children.Analytics.children.Reports.children.CagingCalendar.children.year',
                },
              ],
              element: (
                <PageLayout
                  documentTitle={t(['Site.Caging', 'Site.Calendar'])}
                  title={s(['Site.Caging', 'Site.Calendar'])}>
                  <CagingCalendarProvider>
                    <CagingCalendarPage />
                  </CagingCalendarProvider>
                </PageLayout>
              ),
              icon: ['fas', 'calendar-days'],
              includeInMenu: true,
              path: 'Root.children.Analytics.children.Reports.children.CagingCalendar.path',
              roles: ['devops'],
              title: s(['Site.Caging', 'Site.Calendar']),
            },
            {
              children: [
                {
                  element: <ListPerformancePage />,
                  index: true,
                  loader: listPerformanceLoader,
                },
                {
                  element: <ListPerformancePage />,
                  loader: listPerformanceLoader,
                  path: 'Root.children.Analytics.children.Reports.children.ListPerformance.children.filter',
                },
              ],
              element: <RouteGuard role={['admin']} />,
              icon: ['fas', 'chart-gantt'],
              includeInMenu: true,
              path: 'Root.children.Analytics.children.Reports.children.ListPerformance.path',
              roles: ['admin'],
              title: 'List Performance',
            },
            {
              children: [
                {
                  element: <ShippingLogPage />,
                  index: true,
                },
              ],
              element: <RouteGuard role={['devops']} />,
              icon: ['fas', 'truck-fast'],
              includeInMenu: true,
              path: 'Root.children.Analytics.children.Reports.children.ShippingLog',
              roles: ['devops'],
              title: 'Shipping Log',
            },
          ],
          element: <RouteGuard role={['devops']} />,
        },
      ],
      element: <RouteGuard role={['devops']} />,
      icon: ['fas', 'folder'],
      includeInMenu: true,
      path: 'Root.children.Analytics.children.Reports.path',
      roles: ['devops'],
      title: s(['Site.Report'], { plural: true }),
    },
    {
      children: [
        {
          element: <AnalyticsPage />,
          index: true,
        },
      ],
      element: <RouteGuard role={['devops']} />,
      icon: ['fas', 'chart-simple'],
      includeInMenu: true,
      path: 'Root.children.Analytics.children.Insights',
      roles: ['devops'],
      title: s('Site.Insights'),
    },
    {
      children: [
        {
          element: <AnalyticsPage />,
          index: true,
        },
      ],
      element: <RouteGuard role={['devops']} />,
      icon: ['fas', 'share-nodes'],
      includeInMenu: true,
      path: 'Root.children.Analytics.children.Models',
      roles: ['devops'],
      title: s('Site.Models'),
    },
  ],
  element: <RouteGuard role={['devops']} />,
  icon: ['fas', 'chart-line'],
  includeInMenu: true,
  path: 'Root.children.Analytics.path',
  roles: ['devops'],
  title: s('Site.Analytics'),
};
